import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { IntelligenceEngineApi } from "@src/services/extractionApi";
import { useProcessNotifications } from "./useProcessNotifications";
import { useFileUpload } from "./useFileUpload";
import { useResultsManagement } from "./useResultsManagement";
import { usePageNavigation } from "./usePageNavigation";
import { useToast, type ToastState } from "@hooks/useToast";
import type {
  IProcessInfoViewModel,
  IProcessItemViewModel,
  IExtractedNodeViewModel,
  IExtractedResultViewModel,
  IProcessedResultsFilterViewModel,
  SupportedDownloadFileType,
} from "@models/index";

export interface ExtractionOptions {
  pageNumbers?: string;
  instructions?: string;
  /** Per-run opt-in to mapping. Only offered when the doctype has an enabled
   *  mapping_config; defaults off. Drives whether the backend triggers mapping
   *  and whether the mapping status/redirect UI shows for this process. */
  mappingEnabled?: boolean;
}

export interface IExtractionState {
  startExtraction: (file: File, options?: ExtractionOptions) => Promise<void>;
  getExtractedResult: (
    processInfo: IProcessInfoViewModel,
    item?: IProcessItemViewModel,
  ) => Promise<void>;
  setSelectedProcessResultInfo: (processResult: IProcessInfoViewModel) => void;
  selectPage: (page: number) => void;
  selectNode: (node: IExtractedNodeViewModel) => void;
  getProcessedResultsInfo: (
    doctypeCode: string,
    filter?: IProcessedResultsFilterViewModel,
  ) => Promise<void>;
  loadMoreProcesses: (cursor: string) => Promise<void>;
  setDoctypeCode: (code: string) => void;
  downloadResult: (
    processId: string,
    itemId: string,
    fileType?: SupportedDownloadFileType,
  ) => Promise<void>;
  downloadBatchResults: (processId: string) => Promise<void>;
  downloadInputFile: (processId: string) => Promise<void>;
  deleteProcess: (processId: string) => Promise<boolean>;
  deleteProcessItem: (processId: string, itemId: string) => Promise<boolean>;
  resetState: () => void;
  doctypeCode: string;
  selectedNode?: IExtractedNodeViewModel;
  extractedResult?: IExtractedResultViewModel;
  processResultsInfo: IProcessInfoViewModel[];
  selectedProcessResultInfo?: IProcessInfoViewModel;
  selectedProcessItem?: IProcessItemViewModel;
  nextCursor: string | null;
  hasMore: boolean;
  totalPages: number;
  currentPage: number;
  isExtracting: boolean;
  isLoadingResult: boolean;
  isLoadingResults: boolean;
  uploadProgress: number;
  error: string | null;
  toast: ToastState;
  hideToast: () => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}

export const useExtraction = (): IExtractionState => {
  // State management
  const [extractedResult, setExtractedResult] =
    useState<IExtractedResultViewModel>();
  const [processResultsInfo, setProcessResultsInfo] = useState<
    IProcessInfoViewModel[]
  >([]);
  const [selectedProcessResultInfo, setSelectedProcessResultInfo] =
    useState<IProcessInfoViewModel>();
  const [selectedProcessItem, setSelectedProcessItem] =
    useState<IProcessItemViewModel>();
  const [isExtracting, setIsExtracting] = useState(false);
  const [isLoadingResult, setIsLoadingResult] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [doctypeCode, setDoctypeCode] = useState<string>("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedNode, setSelectedNode] = useState<IExtractedNodeViewModel>();
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setProcessResultsInfo([]);
    setExtractedResult(undefined);
    setSelectedNode(undefined);
    setSelectedProcessItem(undefined);
    setCurrentPage(1);
    setTotalPages(0);
    setNextCursor(null);
    setHasMore(false);
    setError(null);
  }, []);

  const intelligenceEngineApi = useMemo(() => new IntelligenceEngineApi(), []);
  const { toast, showError, showSuccess, hideToast } = useToast();

  // Process IDs whose file upload is still in flight locally. The backend
  // creates the process record at presigned-URL time, so any list refetch
  // that lands during the upload would include it as PENDING and flash the
  // row before the progress bar finishes. We filter these IDs out of refetch
  // results until the local upload completes and inserts the row itself.
  const uploadingProcessIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    resetState();
  }, [doctypeCode, resetState]);

  const { connectToProcessNotifications, connectAndWaitForAuth } =
    useProcessNotifications(
      doctypeCode,
      intelligenceEngineApi,
      setProcessResultsInfo,
      showError,
    );

  const { startExtraction } = useFileUpload(
    intelligenceEngineApi,
    setIsExtracting,
    setError,
    setProcessResultsInfo,
    connectAndWaitForAuth,
    showError,
    showSuccess,
    setUploadProgress,
    doctypeCode,
    uploadingProcessIdsRef,
  );

  const {
    getProcessedResultsInfo,
    loadMoreProcesses,
    getExtractedResult,
    downloadResult,
    downloadBatchResults,
    deleteProcess,
    deleteProcessItem,
  } = useResultsManagement(
    intelligenceEngineApi,
    setIsLoadingResults,
    setIsLoadingResult,
    setError,
    setProcessResultsInfo,
    setExtractedResult,
    setSelectedProcessResultInfo,
    setSelectedProcessItem,
    setTotalPages,
    setCurrentPage,
    setSelectedNode,
    setNextCursor,
    setHasMore,
    connectToProcessNotifications,
    showError,
    uploadingProcessIdsRef,
  );

  const downloadInputFile = useCallback(
    async (processId: string) => {
      try {
        const downloadedFile =
          await intelligenceEngineApi.downloadInputFile(processId);
        intelligenceEngineApi.downloadFile(downloadedFile);
      } catch (error) {
        const errorMessage = `Failed to download input file: ${
          error instanceof Error ? error.message : "Unknown error"
        }`;
        setError(errorMessage);
        showError(errorMessage);
        throw error;
      }
    },
    [intelligenceEngineApi, setError, showError],
  );

  const { selectPage, selectNode } = usePageNavigation(
    extractedResult,
    totalPages,
    currentPage,
    setCurrentPage,
    setSelectedNode,
  );

  return {
    startExtraction,
    getExtractedResult,
    setSelectedProcessResultInfo,
    selectPage,
    selectNode,
    getProcessedResultsInfo,
    loadMoreProcesses,
    setDoctypeCode,
    downloadResult,
    downloadBatchResults,
    downloadInputFile,
    deleteProcess,
    deleteProcessItem,
    resetState,
    doctypeCode,
    selectedNode,
    extractedResult,
    processResultsInfo,
    selectedProcessResultInfo,
    selectedProcessItem,
    nextCursor,
    hasMore,
    totalPages,
    currentPage,
    isExtracting,
    isLoadingResult,
    isLoadingResults,
    uploadProgress,
    error,
    toast,
    hideToast,
    showSuccess,
    showError,
  };
};
